import { useEffect, useState } from 'react';

import { DropdownEntry } from '../common/types';
import { IBaseFieldProps } from '../../components/formInputs/commonTypes';
import adWriter from '../../data/adWriterFields.json';
import bioGenerator from '../../data/bioGeneratorFields.json';
import rephraser from '../../data/rephraserFields.json';
import marketingSlogan from '../../data/sloganFields.json';
import businessName from '../../data/businessNameFields.json';
import instaHashtag from '../../data/instaHashtagFields.json';
import productDescription from '../../data/productDescriptionFields.json';
import emailWriterBio from '../../data/emailWriterFields.json';
import instagramCaption from '../../data/instagramCaptionFields.json';
import pageOutline from '../../data/pageOutlineFields.json';
import metaData from '../../data/metaDataFields.json';
import summarizer from '../../data/summarizeFields.json';
import ideaGenerator from '../../data/idea-generator-fields';
import blogIdeaGenerator from '../../data/idea-generator-fields/blog';
import bookIdeaGenerator from '../../data/idea-generator-fields/book';
import businessIdeaGenerator from '../../data/idea-generator-fields/business';
import contentIdeaGenerator from '../../data/idea-generator-fields/content';
import drawingIdeaGenerator from '../../data/idea-generator-fields/drawing';
import giftIdeaGenerator from '../../data/idea-generator-fields/gift';
import paintingIdeaGenerator from '../../data/idea-generator-fields/painting';
import sideHustleIdeaGenerator from '../../data/idea-generator-fields/side-hustle';
import startupIdeaGenerator from '../../data/idea-generator-fields/startup';
import storyIdeaGenerator from '../../data/idea-generator-fields/story';
import articleWriter from '../../data/articleWriterFields.json';
import storyGenerator from '../../data/storyGeneratorFields.json';
import titleGenerator from '../../data/title-generator-fields/index';
import blogTitleGenerator from '../../data/title-generator-fields/blog';
import bookTitleGenerator from '../../data/title-generator-fields/book';
import contentTitleGenerator from '../../data/title-generator-fields/content';
import documentTitleGenerator from '../../data/title-generator-fields/document';
import landingPageTitleGenerator from '../../data/title-generator-fields/landing-page';
import socialMediaPostTitleGenerator from '../../data/title-generator-fields/social-media-post';
import storyTitleGenerator from '../../data/title-generator-fields/story';
import videoTitleGenerator from '../../data/title-generator-fields/video';
import translator from '../../data/translatorFields.json';
import grammarChecker from '../../data/grammarChecker.json';
import essayWriter from '../../data/essayWriterFields.json';
import createFields from '../../data/createFields.json';
import promptGenerator from '../../data/prompt-generator-fields';
import textPromptGenerator from '../../data/prompt-generator-fields/text';
import imagePromptGenerator from '../../data/prompt-generator-fields/image';
import videoPromptGenerator from '../../data/prompt-generator-fields/video';
import codePromptGenerator from '../../data/prompt-generator-fields/code';
import paragraphGenerator from '../../data/paragraph-generator-fields';
import introductionParagraphGenerator from '../../data/paragraph-generator-fields/introduction';
import bodyParagraphGenerator from '../../data/paragraph-generator-fields/body';
import conclusionParagraphGenerator from '../../data/paragraph-generator-fields/conclusion';
import videoScriptFields from '../../data/videoScriptFields.json';
import keywordsGeneratorFields from '../../data/keywordsGeneratorFields.json';
import expandTextFields from '../../data/expandTextFields.json';
import shortenTextFields from '../../data/shortenTextFields.json';
import {
  AD_WRITER_CATEGORY,
  BUSINESS_NAME_CATEGORY,
  INSTAGRAM_HASHTAG_CATEGORY,
  MARKETING_SLOGAN_CATEGORY,
  PRODUCT_DESCRIPTION_CATEGORY,
  REPHRASER_CATEGORY,
  SOCIAL_MEDIA_BIO_CATEGORY,
  INSTAGRAM_CAPTION_CATEGORY,
  PAGE_OUTLINE_CATEGORY,
  META_DATA_MAKER_CATEGORY,
  SUMMARIZER_CATEGORY,
  EMAIL_WRITER_CATEGORY,
  IDEA_GENERATOR_CATEGORY,
  ARTICLE_WRITER_CATEGORY,
  STORY_GENERATOR_CATEGORY,
  TITLE_GENERATOR_CATEGORY,
  BLOG_TITLE_GENERATOR_CATEGORY,
  BOOK_TITLE_GENERATOR_CATEGORY,
  CONTENT_TITLE_GENERATOR_CATEGORY,
  DOCUMENT_TITLE_GENERATOR_CATEGORY,
  LANDING_PAGE_TITLE_GENERATOR_CATEGORY,
  SOCIAL_MEDIA_POST_TITLE_GENERATOR_CATEGORY,
  STORY_TITLE_GENERATOR_CATEGORY,
  VIDEO_TITLE_GENERATOR_CATEGORY,
  VIDEO_SCRIPT_GENERATOR_CATEGORY,
  TRANSLATOR_CATEGORY,
  GRAMMAR_CHECKER_CATEGORY,
  ESSAY_WRITER_CATEGORY,
  BLOG_IDEA_GENERATOR_CATEGORY,
  BOOK_IDEA_GENERATOR_CATEGORY,
  BUSINESS_IDEA_GENERATOR_CATEGORY,
  CONTENT_IDEA_GENERATOR_CATEGORY,
  DRAWING_IDEA_GENERATOR_CATEGORY,
  GIFT_IDEA_GENERATOR_CATEGORY,
  PAINTING_IDEA_GENERATOR_CATEGORY,
  SIDE_HUSTLE_IDEA_GENERATOR_CATEGORY,
  STARTUP_IDEA_GENERATOR_CATEGORY,
  STORY_IDEA_GENERATOR_CATEGORY,
  CREATE_CATEGORY,
  PROMPT_GENERATOR_CATEGORY,
  TEXT_PROMPT_GENERATOR_CATEGORY,
  IMAGE_PROMPT_GENERATOR_CATEGORY,
  VIDEO_PROMPT_GENERATOR_CATEGORY,
  CODE_PROMPT_GENERATOR_CATEGORY,
  PARAGRAPH_GENERATOR_CATEGORY,
  INTRODUCTION_PARAGRAPH_GENERATOR_CATEGORY,
  BODY_PARAGRAPH_GENERATOR_CATEGORY,
  CONCLUSION_PARAGRAPH_GENERATOR_CATEGORY,
  KEYWORD_GENERATOR_CATEGORY,
  EXPAND_TEXT_CATEGORY,
  SHORTEN_TEXT_CATEGORY,
} from '../../constants';

export type FormFieldProperties = IBaseFieldProps & {
  formProps?: any;
  placeholder: string;
  textLabel: string;
  mobileTextLabel?: string;
  hasErrorValidation: boolean;
  warningMessage: string;
  information: string;
  selectedCategory: number;
  required: boolean;
  labelPaddingTop?: number;
  labelPaddingTopMobile?: number;
  defaultToneValue?: {
    title: string;
    value: string;
  };
  dropdownValues?: DropdownEntry[];
  tooltipProps?: {
    width: number;
    height: number;
    top: number;
    left: number;
    arrowWidth: number;
    arrowMarginLeft: number;
    mobileBreakpoints: {
      left: number;
      arrowMarginLeft: number;
    };
  };
  pillsValues?: DropdownEntry[];
  chipValues?: DropdownEntry[];
  defaultChipValue?: {
    title: string;
    value: string;
  };
  seoRadioValue?: boolean;
};

export type GenericFormField = {
  type: 'dropdown' | 'input' | 'textarea' | 'pills' | 'chips' | 'pillsDropdown' | 'checkbox';
  properties: FormFieldProperties;
};

type TranslatorDropdownProperties = IBaseFieldProps & {
  from: FormFieldProperties;
  to: FormFieldProperties;
};

export type TranslatorDropdownFields = {
  type: 'translatorDropdown';
  properties: TranslatorDropdownProperties;
};

export type FormFieldsUnion = GenericFormField | TranslatorDropdownFields;

function useFormFieldsByCategory(selectedCategory: number) {
  const [obj, setObj] = useState<FormFieldsUnion[]>([]);

  useEffect(() => {
    switch (selectedCategory) {
      case AD_WRITER_CATEGORY:
        setObj(adWriter as GenericFormField[]);
        break;
      case SOCIAL_MEDIA_BIO_CATEGORY:
        setObj(bioGenerator as GenericFormField[]);
        break;
      case REPHRASER_CATEGORY:
        setObj(rephraser as GenericFormField[]);
        break;
      case MARKETING_SLOGAN_CATEGORY:
        setObj(marketingSlogan as GenericFormField[]);
        break;
      case BUSINESS_NAME_CATEGORY:
        setObj(businessName as GenericFormField[]);
        break;
      case INSTAGRAM_HASHTAG_CATEGORY:
        setObj(instaHashtag as GenericFormField[]);
        break;
      case PRODUCT_DESCRIPTION_CATEGORY:
        setObj(productDescription as GenericFormField[]);
        break;
      case EMAIL_WRITER_CATEGORY:
        setObj(emailWriterBio as GenericFormField[]);
        break;
      case INSTAGRAM_CAPTION_CATEGORY:
        setObj(instagramCaption as GenericFormField[]);
        break;
      case PAGE_OUTLINE_CATEGORY:
        setObj(pageOutline as GenericFormField[]);
        break;
      case META_DATA_MAKER_CATEGORY:
        setObj(metaData as GenericFormField[]);
        break;
      case SUMMARIZER_CATEGORY:
        setObj(summarizer as GenericFormField[]);
        break;
      case IDEA_GENERATOR_CATEGORY:
        setObj(ideaGenerator as GenericFormField[]);
        break;
      case BLOG_IDEA_GENERATOR_CATEGORY:
        setObj(blogIdeaGenerator as GenericFormField[]);
        break;
      case BOOK_IDEA_GENERATOR_CATEGORY:
        setObj(bookIdeaGenerator as GenericFormField[]);
        break;
      case BUSINESS_IDEA_GENERATOR_CATEGORY:
        setObj(businessIdeaGenerator as GenericFormField[]);
        break;
      case CONTENT_IDEA_GENERATOR_CATEGORY:
        setObj(contentIdeaGenerator as GenericFormField[]);
        break;
      case DRAWING_IDEA_GENERATOR_CATEGORY:
        setObj(drawingIdeaGenerator as GenericFormField[]);
        break;
      case GIFT_IDEA_GENERATOR_CATEGORY:
        setObj(giftIdeaGenerator as GenericFormField[]);
        break;
      case PAINTING_IDEA_GENERATOR_CATEGORY:
        setObj(paintingIdeaGenerator as GenericFormField[]);
        break;
      case SIDE_HUSTLE_IDEA_GENERATOR_CATEGORY:
        setObj(sideHustleIdeaGenerator as GenericFormField[]);
        break;
      case STARTUP_IDEA_GENERATOR_CATEGORY:
        setObj(startupIdeaGenerator as GenericFormField[]);
        break;
      case STORY_IDEA_GENERATOR_CATEGORY:
        setObj(storyIdeaGenerator as GenericFormField[]);
        break;
      case ARTICLE_WRITER_CATEGORY:
        setObj(articleWriter as GenericFormField[]);
        break;
      case STORY_GENERATOR_CATEGORY:
        setObj(storyGenerator as GenericFormField[]);
        break;
      case TRANSLATOR_CATEGORY:
        setObj(translator as TranslatorDropdownFields[]);
        break;
      case GRAMMAR_CHECKER_CATEGORY:
        setObj(grammarChecker as GenericFormField[]);
        break;
      case TITLE_GENERATOR_CATEGORY:
        setObj(titleGenerator as GenericFormField[]);
        break;
      case BLOG_TITLE_GENERATOR_CATEGORY:
        setObj(blogTitleGenerator as GenericFormField[]);
        break;
      case BOOK_TITLE_GENERATOR_CATEGORY:
        setObj(bookTitleGenerator as GenericFormField[]);
        break;
      case CONTENT_TITLE_GENERATOR_CATEGORY:
        setObj(contentTitleGenerator as GenericFormField[]);
        break;
      case DOCUMENT_TITLE_GENERATOR_CATEGORY:
        setObj(documentTitleGenerator as GenericFormField[]);
        break;
      case LANDING_PAGE_TITLE_GENERATOR_CATEGORY:
        setObj(landingPageTitleGenerator as GenericFormField[]);
        break;
      case SOCIAL_MEDIA_POST_TITLE_GENERATOR_CATEGORY:
        setObj(socialMediaPostTitleGenerator as GenericFormField[]);
        break;
      case STORY_TITLE_GENERATOR_CATEGORY:
        setObj(storyTitleGenerator as GenericFormField[]);
        break;
      case VIDEO_TITLE_GENERATOR_CATEGORY:
        setObj(videoTitleGenerator as GenericFormField[]);
        break;
      case VIDEO_SCRIPT_GENERATOR_CATEGORY:
        setObj(videoScriptFields as GenericFormField[]);
        break;
      case ESSAY_WRITER_CATEGORY:
        setObj(essayWriter as GenericFormField[]);
        break;
      case CREATE_CATEGORY:
        setObj(createFields as GenericFormField[]);
        break;
      case PROMPT_GENERATOR_CATEGORY:
        setObj(promptGenerator as GenericFormField[]);
        break;
      case TEXT_PROMPT_GENERATOR_CATEGORY:
        setObj(textPromptGenerator as GenericFormField[]);
        break;
      case IMAGE_PROMPT_GENERATOR_CATEGORY:
        setObj(imagePromptGenerator as GenericFormField[]);
        break;
      case VIDEO_PROMPT_GENERATOR_CATEGORY:
        setObj(videoPromptGenerator as GenericFormField[]);
        break;
      case CODE_PROMPT_GENERATOR_CATEGORY:
        setObj(codePromptGenerator as GenericFormField[]);
        break;
      case PARAGRAPH_GENERATOR_CATEGORY:
        setObj(paragraphGenerator as GenericFormField[]);
        break;
      case INTRODUCTION_PARAGRAPH_GENERATOR_CATEGORY:
        setObj(introductionParagraphGenerator as GenericFormField[]);
        break;
      case BODY_PARAGRAPH_GENERATOR_CATEGORY:
        setObj(bodyParagraphGenerator as GenericFormField[]);
        break;
      case CONCLUSION_PARAGRAPH_GENERATOR_CATEGORY:
        setObj(conclusionParagraphGenerator as GenericFormField[]);
        break;
      case KEYWORD_GENERATOR_CATEGORY:
        setObj(keywordsGeneratorFields as GenericFormField[]);
        break;
      case EXPAND_TEXT_CATEGORY:
        setObj(expandTextFields as GenericFormField[]);
        break;
      case SHORTEN_TEXT_CATEGORY:
        setObj(shortenTextFields as GenericFormField[]);
        break;
    }
  }, [selectedCategory]);

  return obj;
}

export default useFormFieldsByCategory;
